import { runCpuBenchmark } from '/fp/performance.js';
import { runPhase1StorageProbes } from '/fp/persistent-storage.js';
import audioFingerprint from '/fp/audio.js';
import { withTimeout } from '/fp/prewarm/with-timeout.mjs';
import { domReady, pageLoaded } from '/fp/prewarm/dom-ready.mjs';
import { compute_gpu_canvas, compute_cpu_canvas, compute_font_canvas } from '/fp/canvas.js';
import { dumpWebGLCore } from '/fp/webgl.js';
import getFonts from '/fp/fonts.js';
import isCanvasResistant from '/fp/resistance.js';
import { pushCollectIncident } from '/fp/collect-incidents.js';

async function audioForCollect() {
  const r = await audioFingerprint();
  if (!r || typeof r !== 'object') return r;
  const out = { retries: r.retries };
  if (r.hash !== undefined) out.hash = r.hash;
  if (r.error !== undefined) out.error = r.error;
  return out;
}

function keepAlways() {
  return false;
}

function audioNeedsRetry(result) {
  return !!(result && result.error);
}

function cpuNeedsRetry(result) {
  return !result || result.workerUnavailable === true;
}

function noHash(result) {
  return !result || typeof result.hash !== 'string';
}

function noContext(result) {
  return !result || Object.keys(result).length === 0;
}

function noVerdict(result) {
  return !result || result.canvas === null;
}

function entry(name, prewarmFactory, retryFactory, needsRetry) {
  return {
    name,
    prewarmFactory,
    retryFactory,
    needsRetry,
    promise: null,
    settled: false,
    result: undefined,
    errors: [],
    source: null,
    prewarmStarted: false,
    incidents: [],
  };
}

const state = {
  started: false,
  cpu: entry(
    'cpuBench',
    () => runCpuBenchmark({ allowMainThread: false }),

    (prev) =>
      runCpuBenchmark(
        prev && prev.workerUnavailable
          ? { allowWorker: false, webWorker: prev.webWorker, webWorkerMs: prev.webWorkerMs }
          : undefined
      ),
    cpuNeedsRetry
  ),
  storage: entry('storageProbes', runPhase1StorageProbes, runPhase1StorageProbes, keepAlways),
  audio: entry('audio', audioForCollect, audioForCollect, audioNeedsRetry),
  render: {},
};

const RENDER_STEP_TIMEOUT_MS = 3000;
const RENDER_DETACHED = ['webgl', 'webgl2', 'resistanceCanvas', 'gpuCanvas', 'cpuCanvas', 'fontCanvas'];
const RENDER_IN_DOCUMENT = ['fonts'];
const RENDER_ORDER = [...RENDER_DETACHED, ...RENDER_IN_DOCUMENT];

const RENDER_PROBES = {
  fonts: { factory: () => getFonts(), needsRetry: noHash },
  webgl: { factory: () => dumpWebGLCore('webgl', 'experimental-webgl'), needsRetry: noContext },
  webgl2: { factory: () => dumpWebGLCore('webgl2', 'experimental-webgl2'), needsRetry: noContext },
  resistanceCanvas: { factory: () => isCanvasResistant(), needsRetry: noVerdict },
  gpuCanvas: { factory: (e) => compute_gpu_canvas(e.incidents), needsRetry: noHash },
  cpuCanvas: { factory: (e) => compute_cpu_canvas(e.incidents), needsRetry: noHash },
  fontCanvas: { factory: (e) => compute_font_canvas(e.incidents), needsRetry: noHash },
};

for (const name of RENDER_ORDER) {
  const e = entry(name, null, null, RENDER_PROBES[name].needsRetry);
  e.prewarmFactory = () => RENDER_PROBES[name].factory(e);
  e.retryFactory = e.prewarmFactory;
  state.render[name] = e;
}

function nextIdle(fn) {
  try {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(fn, { timeout: 500 });
      return;
    }
  } catch (e) {}
  setTimeout(fn, 50);
}

function runRenderQueue(names) {
  return new Promise((resolve) => {
    let i = 0;
    const step = () => {
      while (i < names.length) {
        const e = state.render[names[i++]];
        if (e.source !== null) continue;
        whenVisible(() => {
          if (e.source === null) start(e, e.prewarmFactory, 'prewarm');
          withTimeout(settled(e.promise), RENDER_STEP_TIMEOUT_MS, () => undefined).then(() => nextIdle(step));
        });
        return;
      }
      resolve();
    };
    nextIdle(step);
  });
}

function settled(promise) {
  return Promise.resolve(promise).then(
    () => undefined,
    () => undefined
  );
}

function start(e, factory, source) {
  e.settled = false;
  e.result = undefined;
  e.source = source;
  if (source === 'prewarm') e.prewarmStarted = true;
  let p;
  try {
    p = Promise.resolve(factory());
  } catch (err) {
    p = Promise.reject(err);
  }
  e.promise = p.then(
    (value) => {
      if (value && typeof value === 'object' && Array.isArray(value.incidents) && value.incidents.length) {
        e.incidents.push(...value.incidents.splice(0));
      }
      e.settled = true;
      e.result = value;
      return value;
    },
    (err) => {
      e.settled = false;
      e.promise = null;
      e.errors.push({ err, phase: source });
      throw err;
    }
  );
  e.promise.catch(() => {});
  return e.promise;
}

function consume(e) {
  if (e.promise && e.settled && e.needsRetry(e.result)) {
    const prev = e.result;
    e.promise = null;
    return start(e, () => e.retryFactory(prev), 'collect');
  }
  if (!e.promise) return start(e, () => e.retryFactory(undefined), 'collect');
  if (e.settled) return e.promise;
  const pending = e.promise;
  return pending.then(
    (result) => {
      if (!e.needsRetry(result)) return result;
      return e.promise === pending ? start(e, () => e.retryFactory(result), 'collect') : consume(e);
    },
    () => consume(e)
  );
}

function deliver(e, promise) {
  return promise.then(
    (r) => r,
    (err) => {
      for (let i = e.errors.length - 1; i >= 0; i--) {
        if (e.errors[i].err === err) {
          e.errors.splice(i, 1);
          break;
        }
      }
      throw err;
    }
  );
}

function whenVisible(fn) {
  if (typeof document === 'undefined' || document.hidden !== true) {
    fn();
    return;
  }
  document.addEventListener('visibilitychange', function onVisible() {
    if (document.hidden === true) return;
    document.removeEventListener('visibilitychange', onVisible);
    fn();
  });
}

const RENDER_SHARED = ['webgl', 'webgl2', 'fontCanvas'];

export function renderProbeNames(products) {
  return RENDER_ORDER.filter((name) => products.fp || RENDER_SHARED.indexOf(name) !== -1);
}

export function startCollectPrewarm(opts) {
  if (state.started) return;
  if (typeof window === 'undefined') return;
  state.started = true;
  if (!opts || opts.prewarm === false) return;
  const allowed = Array.isArray(opts.renderNames) ? opts.renderNames : RENDER_ORDER;
  const allow = (name) => allowed.indexOf(name) !== -1;

  if (opts.storage !== false) start(state.storage, state.storage.prewarmFactory, 'prewarm');

  if (opts.audio !== false) {
    whenVisible(() => {
      if (!state.audio.promise) start(state.audio, state.audio.prewarmFactory, 'prewarm');
    });
  }
  let cpuDone = Promise.resolve();
  if (opts.cpuBench) {
    cpuDone = domReady()
      .then(
        () =>
          new Promise((resolve) => {
            whenVisible(() => {
              if (!state.cpu.promise) start(state.cpu, state.cpu.prewarmFactory, 'prewarm');
              resolve(settled(state.cpu.promise));
            });
          })
      )
      .catch(() => {});
  }
  if (opts.render) {
    Promise.all([domReady(), cpuDone])
      .then(() => runRenderQueue(RENDER_DETACHED.filter(allow)))
      .then(() => pageLoaded())
      .then(() => runRenderQueue(RENDER_IN_DOCUMENT.filter(allow)))
      .catch(() => {});
  }
}

export function getRenderResult(name) {
  const e = state.render[name];
  return deliver(e, consume(e));
}

export function getCpuBenchmark() {
  return deliver(state.cpu, consume(state.cpu));
}

export function getStorageProbes(timeoutMs, fallback) {
  const e = state.storage;
  const pending = consume(e);
  pending.catch(() => {});
  const t0 = Date.now();
  const remaining = (ms) => (ms > 0 ? Math.max(1, ms - (Date.now() - t0)) : 0);
  return deliver(
    e,
    Promise.resolve(timeoutMs).then(
      (ms) => withTimeout(pending, remaining(ms), fallback),
      () => withTimeout(pending, 0, fallback)
    )
  );
}

export function getAudioFingerprint() {
  return deliver(state.audio, consume(state.audio));
}

export function getCollectPrewarmHits() {
  const hit = (e) => e.settled && e.source === 'prewarm' && !e.needsRetry(e.result);
  let renderPrewarmHits = 0;
  let renderPrewarmStarted = 0;
  for (const name of RENDER_ORDER) {
    const e = state.render[name];
    if (hit(e)) renderPrewarmHits++;
    if (e.prewarmStarted) renderPrewarmStarted++;
  }
  return {
    cpuBenchPrewarmHit: hit(state.cpu),
    storagePrewarmHit: hit(state.storage),
    audioPrewarmHit: hit(state.audio),
    renderPrewarmHits,
    renderPrewarmStarted,
  };
}

export function takeCollectPrewarmIncidents() {
  const out = [];
  for (const e of [state.cpu, state.storage, state.audio, ...RENDER_ORDER.map((n) => state.render[n])]) {
    for (const { err, phase } of e.errors.splice(0)) pushCollectIncident(out, { source: e.name, phase, error: err });
    if (e.incidents.length) out.push(...e.incidents.splice(0));
  }
  return out;
}
